// Floating command bar — the "command line" for AI edits. Appears when the user
// has a target (a selected passage or the section at the cursor); they type a
// free-form instruction or tap a quick-action chip. Modelled on the marking
// workspace's snip→action popover.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Spinner } from "@/components/ui";

const QUICK_ACTIONS: { label: string; directive: string }[] = [
  { label: "Expand", directive: "Expand this with more depth and detail, grounded in the sources." },
  { label: "Shorten", directive: "Make this more concise without losing key content." },
  { label: "Clarify", directive: "Clarify and improve the wording; fix any awkward phrasing." },
  { label: "Make rigorous", directive: "Make the argument more rigorous and precise, with correct terminology and full justification." },
  { label: "Add an example", directive: "Add a concrete worked example that illustrates the point, grounded in the sources." },
  { label: "Improve transitions", directive: "Improve the flow and transitions so the passage reads smoothly." },
  { label: "Define terms", directive: "Define any technical terms on first use, briefly and precisely." },
  { label: "Add citations", directive: "Add \\cite references where claims are supported by the sources." },
  { label: "Fix LaTeX", directive: "Fix any LaTeX errors or malformed math in this passage." },
];

interface Props {
  targetLabel: string;
  busy: boolean;
  onSubmit: (directive: string) => void;
  onClose: () => void;
}

export function CommandBar({ targetLabel, busy, onSubmit, onClose }: Props) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = () => {
    const v = value.trim();
    if (v && !busy) onSubmit(v);
  };

  return (
    <div className="absolute inset-x-0 bottom-0 z-20 border-t border-black/10 bg-white/95 p-3 shadow-lg backdrop-blur">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="truncate text-xs font-medium text-ink-soft">
          {t("Editing")}: <span className="text-ink">{targetLabel}</span>
        </span>
        <button type="button" onClick={onClose} className="text-xs text-ink-faint hover:text-ink">
          {t("Cancel")}
        </button>
      </div>
      <div className="mb-2 flex flex-wrap gap-1">
        {QUICK_ACTIONS.map((a) => (
          <button
            key={a.label}
            type="button"
            disabled={busy}
            onClick={() => setValue(a.directive)}
            className="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs text-zinc-600 hover:bg-zinc-200"
          >
            {t(a.label)}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          value={value}
          disabled={busy}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") onClose();
          }}
          placeholder={t("Tell Eddi how to edit this…")}
          className="flex-1 rounded-lg border border-black/10 px-3 py-1.5 text-sm focus:border-brand-400 focus:outline-none"
        />
        <Button size="sm" onClick={submit} disabled={busy || !value.trim()}>
          {busy ? <Spinner className="h-3 w-3" /> : t("Ask Eddi")}
        </Button>
      </div>
    </div>
  );
}
